import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../components/Toast';

export default function AcceptInvite() {
  const [searchParams] = useSearchParams();
  const { user, loading, joinCompany, memberships } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();

  const companyCode = searchParams.get('companyCode') ?? '';
  const companyName = searchParams.get('companyName') ?? 'a company';
  const role        = (searchParams.get('role') ?? 'developer') as 'developer' | 'tester';
  const email       = searchParams.get('email') ?? '';

  const [accepting, setAccepting] = useState(false);
  const [done,      setDone]      = useState(false);

  // If already a member of this company, skip straight to dashboard
  const alreadyMember = memberships.some(m => m.companies?.invite_code === companyCode);

  useEffect(() => {
    if (!loading && user && alreadyMember) {
      showToast(`You are already a member of ${companyName}.`, 'success');
      navigate('/', { replace: true });
    }
  }, [loading, user, alreadyMember]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAccept = async () => {
    if (!companyCode) {
      showToast('Invalid invite link — missing company code.', 'error');
      return;
    }
    setAccepting(true);
    const { error, company_name } = await joinCompany(companyCode, role);
    setAccepting(false);
    if (error) {
      const msg = error.message?.includes('Invalid invite code')
        ? 'This invite code is invalid or has expired.'
        : error.message?.includes('Already a member')
        ? `You are already a member of ${companyName}.`
        : error.message || 'Failed to join company.';
      showToast(msg, 'error');
    } else {
      setDone(true);
      showToast(`Welcome to ${company_name ?? companyName}!`, 'success');
    }
  };

  const redirectParam = `?${searchParams.toString()}`;
  const loginUrl  = `/login?redirect=/accept-invite${encodeURIComponent(redirectParam)}`;
  const signupUrl = `/signup?${searchParams.toString()}`;

  const roleBadgeColor: Record<string, string> = {
    admin:     '#1C9B82',
    developer: '#3b82f6',
    tester:    '#f59e0b',
  };

  if (loading) {
    return (
      <div className="auth-page">
        <div className="auth-card" style={{ textAlign: 'center', padding: 48 }}>
          <div className="app-loading-spinner" />
        </div>
      </div>
    );
  }

  // ── Success state ─────────────────────────────────────────────
  if (done) {
    return (
      <div className="auth-page">
        <div className="auth-card">
          <div className="auth-logo" style={{ background: '#dcfce7' }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>
          <h1 className="auth-title">You're in!</h1>
          <p className="auth-subtitle">
            You've joined <strong>{companyName}</strong> as a <strong>{role}</strong>.
          </p>
          <button className="btn btn-primary btn-full" style={{ marginTop: 24 }} onClick={() => navigate('/')}>
            Go to Dashboard →
          </button>
        </div>
      </div>
    );
  }

  // ── Logged-in: show Accept button ─────────────────────────────
  if (user) {
    return (
      <div className="auth-page">
        <div className="auth-card">
          <div className="auth-logo">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/>
              <polyline points="9 22 9 12 15 12 15 22"/>
            </svg>
          </div>
          <h1 className="auth-title">You've been invited</h1>
          <p className="auth-subtitle">
            You've been invited to join
          </p>

          <div className="invite-company-card">
            <div className="invite-company-avatar">
              {companyName.charAt(0).toUpperCase()}
            </div>
            <div className="invite-company-info">
              <div className="invite-company-name">{companyName}</div>
              <span
                className="dropdown-role-badge"
                style={{ background: roleBadgeColor[role] ?? '#6b7280' }}
              >
                {role}
              </span>
            </div>
          </div>

          {email && (
            <p style={{ fontSize: 13, color: 'var(--color-text-muted)', textAlign: 'center', marginBottom: 8 }}>
              Invite sent to <strong>{email}</strong>
            </p>
          )}

          <button
            className="btn btn-primary btn-full"
            style={{ marginTop: 16 }}
            onClick={handleAccept}
            disabled={accepting}
          >
            {accepting ? <><span className="btn-spinner" /> Joining…</> : 'Accept Invitation'}
          </button>

          <button
            className="btn btn-full"
            style={{ marginTop: 10, background: 'none', border: '1px solid var(--color-border)', color: 'var(--color-text)' }}
            onClick={() => navigate('/')}
          >
            Decline
          </button>
        </div>
      </div>
    );
  }

  // ── Not logged in: sign in or create account ──────────────────
  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="auth-logo">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/>
            <polyline points="9 22 9 12 15 12 15 22"/>
          </svg>
        </div>
        <h1 className="auth-title">You've been invited</h1>
        <p className="auth-subtitle">to join</p>

        <div className="invite-company-card">
          <div className="invite-company-avatar">
            {companyName.charAt(0).toUpperCase()}
          </div>
          <div className="invite-company-info">
            <div className="invite-company-name">{companyName}</div>
            <span
              className="dropdown-role-badge"
              style={{ background: roleBadgeColor[role] ?? '#6b7280' }}
            >
              {role}
            </span>
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 24 }}>
          <Link to={loginUrl} className="btn btn-primary btn-full" style={{ textAlign: 'center', textDecoration: 'none' }}>
            Sign in to accept
          </Link>
          <div className="auth-divider"><span>or</span></div>
          <Link to={signupUrl} className="btn btn-full" style={{ textAlign: 'center', textDecoration: 'none', border: '1px solid var(--color-border)', color: 'var(--color-text)', background: 'none' }}>
            Create a new account
          </Link>
        </div>

        <p style={{ fontSize: 12, color: 'var(--color-text-muted)', textAlign: 'center', marginTop: 20 }}>
          Already have an account? Sign in above to accept the invitation without creating a new account.
        </p>
      </div>
    </div>
  );
}
